import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  LoadingBoxBottom,
  styleConstants,
  Tag,
  TagSizes,
  themeConstants,
} from '@paywithextend/components'
import moment from 'moment'
import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'
import BackgroundPage from 'src/components/BackgroundPage/BackgroundPage'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import Box from 'src/components/Box/Box'
import ListBox from 'src/components/ListBox/ListBox'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import UserAvatarAndName from 'src/components/UserAvatarAndName/UserAvatarAndName'
import { useWindowScrollBottomHandler } from 'src/hooks'
import Crncy from 'src/localization/Crncy'
import Trns from 'src/localization/Trns'
import {
  OrgExpenseReport,
  OrgExpenseReportStatus,
  orgExpenseReportStatusToColor,
  orgExpenseReportStatusToString,
} from 'src/models/orgExpenseReports.models'
import {
  orgExpenseReportUrl
} from 'src/navigation/urls'
import {
  useQueryOrgExpenseReportCycle,
  useQueryOrgExpenseReportCycleReports,
} from 'src/queries/orgExpenseReports.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectExpenseReportsHistoricalReportsCycleIdFromRoute } from 'src/redux/selectors/data/orgExpenseReports.selectors'
import { toDollars } from 'src/utils/currency.utils'
import { css } from 'styled-components'

import styles from './StatementsList.style'

const OrgExpenseReportsHistoricalReports: React.FC = () => {
  const {
    show_automated_expense_review: showAutomatedExpenseReview
   } = useSelector(
    selectFeatureFlaggerFromState,
  )

  const cycleId = useSelector(
    selectExpenseReportsHistoricalReportsCycleIdFromRoute,
  )
  const { data: cycle, error: cycleError } = useQueryOrgExpenseReportCycle({
    cycleId,
  })
  const user = useSelector(selectUserListModelFromState)

  const {
    data: cycleReports,
    hasNextPage,
    isFetching,
    error: cycleReportsError,
    fetchNextPage,
  } = useQueryOrgExpenseReportCycleReports({ cycleId })

  const handleScroll = useCallback((): void => {
    if (hasNextPage && !isFetching && !cycleReportsError && !cycleError) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetching, cycleReportsError, cycleError, fetchNextPage])
  useWindowScrollBottomHandler(handleScroll)

  const userAndCardName = cycle !== undefined ? (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          width: 100%;
        `}
      >
        <UserAvatarAndName
          textCss={css`
            ${getFontCss(
              14,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
          `}
          user={{
            id: cycle.accountUserId,
            fullNameOrEmail:
              cycle.accountUserFullName || cycle.accountUserEmail,
            initials:
              `${cycle.accountUserFirstName?.slice(0, 1)}${cycle.accountUserLastName?.slice(0, 1)}` ||
              cycle.accountUserEmail[0],
            avatarUrl: cycle.accountUserAvatarUrl,
            hasFullName: true,
          }}
        />
        <span
          css={css`
            margin-top: 12px;
            margin-bottom: 8px;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                28,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            <span
              css={css`
                padding-inline-end: 12px;
              `}
            >
              {cycle?.accountName}
            </span>
            <Tag
              text={cycle.accountLast4.toString()}
              size={TagSizes.Medium}
            />
          </span>
        </span>
      </div>
  ) : undefined

  return user?.hasOrgBookkeeperPermissions && !cycleError ? (
    <BackgroundPage className="StatementsList" css={styles}>
      <BackgroundPageContent noStackingIndex>
        {cycle === undefined ? (
          <LoadingBackgroundPage />
        ) : (
          <>
            {showAutomatedExpenseReview && (userAndCardName) }
            <Box className="StatementsList statements-container">
              {!showAutomatedExpenseReview && (userAndCardName) }
              <div className="StatementsList statements">
                {cycleReports?.map((report: OrgExpenseReport) => {
                  return (
                    <ListBox
                      key={report.id}
                      id={'report-' + report.id}
                      linkProps={{
                        to: orgExpenseReportUrl(cycleId, report.id),
                        ariaLabelSource: 'Account Expense Report',
                      }}
                      cells={[
                        {
                          label: <Trns source={showAutomatedExpenseReview ? 'Statement Date' : 'Report Date'} />,
                          content: (
                            <div id="title">
                              {moment.utc(report.since).format('LL') +
                                ' - ' +
                                moment.utc(report.until).format('LL')}
                            </div>
                          ),
                        },
                        ...(!showAutomatedExpenseReview
                          ? [
                              {
                                label: <Trns source={'Report Status'} />,
                                content: (
                                  <Tag
                                    text={
                                      <Trns
                                        source={orgExpenseReportStatusToString(
                                          report?.reportStatus ||
                                            OrgExpenseReportStatus.PendingReview,
                                        )}
                                      />
                                    }
                                    color={orgExpenseReportStatusToColor(
                                      report?.reportStatus ||
                                        OrgExpenseReportStatus.PendingReview,
                                    )}
                                    size={TagSizes.Medium}
                                  />
                                ),
                              },
                            ]
                          : []),
                        report.totalSpentCents !== undefined
                          ? {
                              label: (
                                <div
                                  css={css`
                                    text-align: ${showAutomatedExpenseReview ? 'right' : 'left'};
                                  `}>
                                  <Trns source={'Spend'} />
                                </div>
                              ),
                              content: (
                                <Crncy
                                  currency={report.accountCurrency}
                                  amount={toDollars(report.totalSpentCents)}
                                />
                              ),
                            }
                          : {
                              label: <></>,
                              content: <></>,
                            },
                      ]}
                      icon={
                        <div
                          css={css`
                            display: flex;
                            align-items: center;
                          `}
                        >
                          <SvgChevronRight
                            id={'chevron'}
                            css={css`
                              transition: transform
                                ${styleConstants.transitionSpeed.fast} ease-out;
                              transform: scale(calc(20 / 24));
                              ${getBreakpointDown(breakpointSizes.SM)(css`
                                display: none;
                              `)}
                            `}
                          />
                        </div>
                      }
                    />
                  )
                })}
              </div>
              {isFetching && <LoadingBoxBottom />}
            </Box>
          </>
        )}
      </BackgroundPageContent>
    </BackgroundPage>
  ) : (
    <NotFoundBackgroundPage />
  )
}

export default OrgExpenseReportsHistoricalReports
